import React, { useState, useEffect } from 'react';
import axios from '../../config/axiosInstance';
import { Table, Button, Space, Card, Row, Col, message, Tooltip, Popconfirm, Input, Tag, Select, Modal, QRCode } from 'antd';
import {
    EditOutlined, DeleteOutlined, AppleOutlined, WindowsOutlined, AndroidOutlined, CloudTwoTone, MessageOutlined,
    ThunderboltOutlined, EnvironmentOutlined, QrcodeOutlined
} from '@ant-design/icons';
import DeviceEditForm from './DeviceEditForm';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { useScreenSize } from '../../context/ScreenSizeContext'; 
import '../../styles/DeviceList.css';
import SendMessagePopup from '../DeviceConfigs/SendMessagePopup';
import ModalBatteryHistory from './ModalBatteryHistory';
import ModalLocationInfo from './ModalLocationInfo';

const { Search } = Input; 

const DeviceListAdmin = () => {
  const { hasFeature } = useAuth();
  const { darkTheme } = useTheme(); 
  const [devices, setDevices] = useState([]);
  const [deviceCounts, setDeviceCounts] = useState({
    iOS: 0,
    Android: 0,
    WinUI: 0, 
  });
  const [selectedPlatforms, setSelectedPlatforms] = useState(new Set());
  const [editingDevice, setEditingDevice] = useState(null); 
  const [searchText, setSearchText] = useState('');
  const [foundDevices, setFoundDevices] = useState([]);
  const { isMobile } = useScreenSize();
  const [sendMessagePopupRecord, setSendMessagePopupRecord] = useState(null);
  const [batteryModalVisible, setBatteryModalVisible] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [locationModalVisible, setLocationModalVisible] = useState(false);
  const [selectedDeviceLocation, setSelectedDeviceLocation] = useState(null);
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [qrModalVisible, setQrModalVisible] = useState(false);
  const [selectedQrDevice, setSelectedQrDevice] = useState(null);
  const canSeeLeadCaptureVersion = hasFeature('deviceConfig.leadcapture');

    useEffect(() => {
        axios.get('/devices')
            .then(response => {
                const data = response.data;
                setDevices(data);

                // Extracting unique events from device configurations
                const uniqueEvents = [];
                const seenEventIds = new Set();

                data.forEach(device => {
                    const event = device.DeviceConfigId?.SelectedEventId;
                    if (event && event._id && !seenEventIds.has(event._id)) {
                        seenEventIds.add(event._id);
                        uniqueEvents.push({
                            _id: event._id,
                            mnemonic: event.mnemonic
                        });
                    }
                });
                setEvents(uniqueEvents);

                // Count devices per platform
                const counts = data.reduce((acc, device) => {
                    const platform = device.Platform;
                    if (platform === 'MacCatalyst') {
                        acc.iOS = (acc.iOS || 0) + 1;
                    } else {
                        acc[platform] = (acc[platform] || 0) + 1;
                    }
                    return acc;
                }, {});

                // Establish counts in the state
                setDeviceCounts({
                    iOS: counts.iOS || 0,
                    Android: counts.Android || 0,
                    WinUI: counts.WinUI || 0,
                });
            })
            .catch(() => {
                setDevices([]);
                setEvents([]);
                setDeviceCounts({ iOS: 0, Android: 0, WinUI: 0 });
            });
    }, []);

    useEffect(() => {
        const lowerCaseSearchText = searchText.toLowerCase();

        const filtered = devices.filter(device => {
            const matchesSearch =
                device.Name.toLowerCase().includes(lowerCaseSearchText) ||
                (device.DeviceConfigId?.DeviceConfigName || '').toLowerCase().includes(lowerCaseSearchText) ||
                device.SerialNumber.toLowerCase().includes(lowerCaseSearchText) ||
                device.Platform.toLowerCase().includes(lowerCaseSearchText) ||
                (device.appVersion || 'unknown').toLowerCase().includes(lowerCaseSearchText) ||
                (device.appVersion || 'unknown').toLowerCase().includes(lowerCaseSearchText) ||
                (
                    canSeeLeadCaptureVersion &&
                    (device.appLCVersion || 'unknown').toLowerCase().includes(lowerCaseSearchText)
                );

            const matchesEvent = !selectedEventId || device.DeviceConfigId?.SelectedEventId?._id === selectedEventId;

            return matchesSearch && matchesEvent;
        });

        setFoundDevices(filtered);

        // We also updated the platform count based on the filtered result.
        const counts = filtered.reduce((acc, device) => {
            const platform = device.Platform === 'MacCatalyst' ? 'iOS' : device.Platform;
            acc[platform] = (acc[platform] || 0) + 1;
            return acc;
        }, {});

        setDeviceCounts({
            iOS: counts.iOS || 0,
            Android: counts.Android || 0,
            WinUI: counts.WinUI || 0,
        });
    }, [searchText, devices, selectedEventId, canSeeLeadCaptureVersion]);

  useEffect(() => {
    const fetchUpdatedDevices = async () => {
      try {
        const response = await axios.get('/devices');
        setDevices(response.data);
      } catch (error) {
        setDevices([]);
      }
    };
  
    // We call the function to update the devices every 10 seconds, for example.
    const intervalId = setInterval(fetchUpdatedDevices, 10000);
  
    // We clean the interval when the component is disassembled
    return () => clearInterval(intervalId);
  }, []); 

  // Map platform names to corresponding icons for Table
  const platformIcons = {
    iOS: <AppleOutlined style={{ fontSize: '24px', color: darkTheme ? '#FFFFFF' : '#000000' }} />,
    Android: <AndroidOutlined style={{ fontSize: '24px', color: darkTheme ? '#A4C639' : '#3DDC84' }} />,
    WinUI: <WindowsOutlined style={{ fontSize: '24px', color: darkTheme ? '#FFFFFF' : '#0078D6' }} />,
    MacCatalyst: <AppleOutlined style={{ fontSize: '24px', color: darkTheme ? '#FFFFFF' : '#000000' }} />,
  };

  // Manages platform selection
  const handlePlatformClick = (platform) => {
    setSelectedPlatforms(prev => {
      const updated = new Set(prev);
      if (updated.has(platform)) {
        updated.delete(platform);
      } else {
        updated.add(platform);
      }
      return updated;
    });
  };

  // Filter devices according to selected platforms
  const filteredDevices = foundDevices.filter(device => 
    selectedPlatforms.size === 0 || selectedPlatforms.has(device.Platform) || (device.Platform === 'MacCatalyst' && selectedPlatforms.has('iOS'))
  );

  // Define the columns of the table
  const columns = [
  {
          title: (
              <span>
      <CloudTwoTone />
    </span>
          ),
          dataIndex: 'wsActive',
          key: 'wsActive',
          sorter: (a, b) => {
              return (a.wsActive === b.wsActive) ? 0 : a.wsActive ? -1 : 1;
          },
          render: (wsActive) => (
              <span style={{ position: 'relative', display: 'inline-block', width: '15px', height: '15px' }}>
                {/* Background circle (lighter outline) */}
                  <span
                      style={{
                          width: '15px',
                          height: '15px',
                          borderRadius: '50%',
                          backgroundColor: wsActive ? 'rgba(16, 185, 129, 0.2)' : 'rgba(255, 0, 0, 0.2)',
                          position: 'absolute',
                          top: 0,
                          left: 0
                      }}
                  />
                  {/* Front circle (active or inactive indicator) */}
                  <span
                      style={{
                          width: '10px',
                          height: '10px',
                          borderRadius: '50%',
                          backgroundColor: wsActive ? '#10B981' : '#EF4444',
                          position: 'absolute',
                          top: '2.5px',
                          left: '2.5px'
                      }}
                  />
              </span>
          ),
      },
      {
          title: 'Name',
          dataIndex: 'Name',
          key: 'Name',
          sorter: (a, b) => a.Name.localeCompare(b.Name),
          render: (text, record) => (
              <Tooltip title={record.SerialNumber}>
                  <span>{text}</span>
              </Tooltip>
          ),
      },
      {
          title: 'Device Config Name',
          dataIndex: ['DeviceConfigId', 'DeviceConfigName'],
          key: 'DeviceConfigName',
          responsive: ['md', 'lg'],
          sorter: (a, b) => {
              const configNameA = a.DeviceConfigId?.DeviceConfigName || '';
              const configNameB = b.DeviceConfigId?.DeviceConfigName || '';
              return configNameA.localeCompare(configNameB);
          },
          render: (text) => (
              <Tag color={text ? 'blue' : 'red'}>
                  {text || 'Not Assigned'}
              </Tag>
          ),
      },
      {
          title: 'Platform',
          dataIndex: 'Platform',
          key: 'Platform',
          render: (platform) => (
              <span>{platformIcons[platform] || 'Unknown'}</span>
          ),
      },
      {
          title: 'App Version',
          dataIndex: 'appVersion',
          key: 'appVersion',
          sorter: (a, b) => (a.appVersion || '').localeCompare(b.appVersion || ''),
          render: (version) => (
              <Tag color={(!version || version === 'unknown') ? 'red' : 'blue'}>
                  {version || 'unknown'}
              </Tag>
          ),
      },
      canSeeLeadCaptureVersion && {
          title: 'LC Version',
          dataIndex: 'appLCVersion',
          key: 'appLCVersion',
          sorter: (a, b) => (a.appLCVersion || '').localeCompare(b.appLCVersion || ''),
          render: (version) => (
              <Tag color={(!version || version === 'unknown') ? 'red' : 'blue'}>
                  {version || 'unknown'}
              </Tag>
          ),
      },
      (hasFeature('devices.battery') || hasFeature('devices.location')) && {
          title: 'Status',
          key: 'status',
          render: (_, record) => {
              const level = record.batteryStatus?.level;
              const state = record.batteryStatus?.state;
              const showBattery = hasFeature('devices.battery') && level != null && record.wsActive;
              const showLocation = hasFeature('devices.location') &&
                  record.locationStatus &&
                  record.locationStatus.latitude &&
                  record.locationStatus.longitude;

              const batteryColor =
                  level >= 50 ? 'green' : level >= 20 ? 'orange' : 'red';

              return (
                  <Space>
                      {/* Invisible placeholders keep every slot occupied, so the
                          status icons line up across rows like in a grid */}
                      {showBattery ? (
                          <Tooltip title={`${state || 'Unknown'} (${level}%)`}>
                              <Tag color={batteryColor} style={{ minWidth: 48, textAlign: 'center' }}>
                                  {level}%{' '}
                              </Tag>
                          </Tooltip>
                      ) : (
                          <Tag style={{ minWidth: 48, visibility: 'hidden' }}>0%</Tag>
                      )}

                      {hasFeature('devices.battery') && level != null ? (
                          <Tooltip title="Battery Info">
                              <Button
                                  type="text"
                                  size="small"
                                  icon={<ThunderboltOutlined style={{ color: '#FACC15' }} />}
                                  onClick={() => {
                                      setSelectedDevice(record);
                                      setBatteryModalVisible(true);
                                  }}
                              />
                          </Tooltip>
                      ) : (
                          <Button type="text" size="small" style={{ visibility: 'hidden' }} icon={<ThunderboltOutlined />} />
                      )}

                      {showLocation ? (
                          <Tooltip title="Last location">
                              <Button
                                  type="text"
                                  size="small"
                                  icon={<EnvironmentOutlined style={{ color: darkTheme ? '#69B1FF' : '#1677ff' }} />}
                                  onClick={() => {
                                      setSelectedDeviceLocation(record);
                                      setLocationModalVisible(true);
                                  }}
                              />
                          </Tooltip>
                      ) : (
                          <Button type="text" size="small" style={{ visibility: 'hidden' }} icon={<EnvironmentOutlined />} />
                      )}
                  </Space>
              );
          },
      },
      {
          title: 'Actions',
          key: 'actions',
          render: (_, record) => (
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Space size="middle">

                  {record.wsActive ? (
                    <Tooltip title="Send message" color="green">
                        <Button
                            icon={<MessageOutlined style={{ color: 'green' }} />}
                            onClick={() => handleSendMessage(record)}
                        />
                    </Tooltip>
                  ) : (
                    // Invisible placeholder so the action buttons line up across rows
                    <Button style={{ visibility: 'hidden' }} icon={<MessageOutlined />} />
                  )}

                  <Tooltip title="Show QR" color="purple">
                    <Button
                      icon={<QrcodeOutlined />}
                      onClick={() => handleShowQr(record)}
                    />
                  </Tooltip>

                  <Tooltip title="Edit" color="blue">
                    <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} />
                  </Tooltip>


                  <Popconfirm
                    title="Are you sure you want to delete this device?"
                    onConfirm={() => handleDelete(record)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Tooltip title="Delete" color="red">
                      <Button
                        icon={<DeleteOutlined />}
                      />
                    </Tooltip>
                  </Popconfirm>

                </Space>
            </div>
          ),
      },
  ];

  const handleEdit = (record) => {
    setEditingDevice(record); 
  };
  const handleModalClose = () => {
    setEditingDevice(null); 
  };

  const handleDelete = async (record) => {
    try {
      await axios.delete(`/devices/${record._id}`);
      
      // Update local status after device deletion
      setDevices(prevDevices => prevDevices.filter(device => device._id !== record._id));
      message.success(`Device ${record.Name} has been deleted successfully.`);
    } catch (error) {
      message.error('Error deleting device. Please try again.');
    }
  };

  const handleSendMessage = (record) => {
      setSendMessagePopupRecord({ ...record, type: 'device' });
  };

  const handleShowQr = (record) => {
    setSelectedQrDevice(record);
    setQrModalVisible(true);
  };

    return (
    <div style={{ padding: '24px' }}>
      <h1 style={{ marginBottom: '24px' }}>Devices List</h1>
      {/* Platform cards */}
      <Row gutter={16} style={{ marginBottom: '48px' }}> 
        <Col span={8}>
          <Card 
            bordered={false} 
            className={`platform-card ${darkTheme ? 'platform-card-dark' : 'platform-card-light'} ${selectedPlatforms.has('iOS') ? 'selected' : ''}`}
            onClick={() => handlePlatformClick('iOS')}
          >
            <div className="platform-card-content">
              <AppleOutlined 
                className="platform-icon" 
                style={{ 
                  fontSize: '48px', 
                  color: darkTheme ? '#FFFFFF' : '#000000',
                  marginBottom: '15px'
                }} 
              />
              {!isMobile ? <Card.Meta title="Apple" /> : null}
              <Card.Meta 
                description={isMobile ? String(deviceCounts.iOS || 0) : `Devices: ${deviceCounts.iOS || 0}`} 
                style={{ marginTop: '10px' }} 
              />
            </div>
          </Card>
        </Col>
        <Col span={8}>
          <Card 
            bordered={false} 
            className={`platform-card ${darkTheme ? 'platform-card-dark' : 'platform-card-light'} ${selectedPlatforms.has('WinUI') ? 'selected' : ''}`}
            onClick={() => handlePlatformClick('WinUI')}
          >
            <div className="platform-card-content">
              <WindowsOutlined 
                className="platform-icon" 
                style={{ 
                  fontSize: '48px', 
                  color: '#0078D6', 
                  marginBottom: '15px'
                }} 
              />
              {!isMobile ? <Card.Meta title="Windows" /> : null}
              <Card.Meta 
                description={isMobile ? String(deviceCounts.WinUI || 0) : `Devices: ${deviceCounts.WinUI || 0}`} 
                style={{ marginTop: '10px' }} 
              />
            </div>
          </Card>
        </Col>
        <Col span={8}>
          <Card 
            bordered={false} 
            className={`platform-card ${darkTheme ? 'platform-card-dark' : 'platform-card-light'} ${selectedPlatforms.has('Android') ? 'selected' : ''}`}
            onClick={() => handlePlatformClick('Android')}
          >
            <div className="platform-card-content">
              <AndroidOutlined 
                className="platform-icon" 
                style={{ 
                  fontSize: '48px', 
                  color: darkTheme ? '#A4C639' : '#3DDC84',
                  marginBottom: '15px'
                }} 
              />
              {!isMobile ? <Card.Meta title="Android" /> : null}
              <Card.Meta 
                description={isMobile ? String(deviceCounts.Android || 0) : `Devices: ${deviceCounts.Android || 0}`} 
                style={{ marginTop: '10px' }} 
              />
            </div>
          </Card>
        </Col>
      </Row>

      {/* Event Select + Search */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
        <Select
            allowClear
            placeholder="Filter by event mnemonic"
            style={{ width: 300 }}
            value={selectedEventId || undefined}
            onChange={(value) => setSelectedEventId(value)}
            options={events.map(event => ({
                label: event.mnemonic,
                value: event._id
            }))}
        />
        <Search
            placeholder="Search devices..."
            onSearch={value => setSearchText(value)}
            onChange={e => setSearchText(e.target.value)}
            style={{ width: 300 }}
        />
      </div>

      {/* Table of devices */}
      <Table
        columns={columns.filter((Boolean))}
        dataSource={filteredDevices}
        rowKey="_id"
        scroll={{ x: 'max-content' }}
      />
      {/* Editing mode */}
      {editingDevice && (
        <DeviceEditForm
          editingDevice={editingDevice}
          onClose={handleModalClose} 
        />
      )}

      <SendMessagePopup
        open={!!sendMessagePopupRecord}
        record={sendMessagePopupRecord}
        onClose={() => setSendMessagePopupRecord(null)}
      />

      <ModalBatteryHistory
        open={batteryModalVisible}
        onClose={() => setBatteryModalVisible(false)}
        device={selectedDevice}
      />

      <ModalLocationInfo
        visible={locationModalVisible}
        onClose={() => setLocationModalVisible(false)}
        device={selectedDeviceLocation}
      />

      <Modal
        open={qrModalVisible}
        width={360}
        onCancel={() => {
            setQrModalVisible(false);
            setSelectedQrDevice(null);
        }}
        footer={null}
        title={
            <Space>
                {platformIcons[selectedQrDevice?.Platform]}
                <span>{selectedQrDevice?.Name || 'Device'} serial number</span>
            </Space>
        }
        centered
      >
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
            {/* QR content is the device serial number */}
            <QRCode
                value={`dsync;${selectedQrDevice?.SerialNumber || ''}`}
                size={180}
            />
            <Tag color="blue">{selectedQrDevice?.SerialNumber || 'No Serial Number'}</Tag>
        </div>
      </Modal>

    </div>
  );
};

export default DeviceListAdmin;
